import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { PlayerSwap } from "@/api/privateRequests/sbcs/player-swap";
import { PitchView } from "@/components/organisms/SolutionView/PitchView";
import { SbcSheetButton } from "@/components/organisms/SolutionView/SbcSheetButton";
import { SolutionActions } from "@/components/organisms/SolutionView/SolutionActions";
import {
  SolutionSidePanel,
  solutionFactsOf,
} from "@/components/organisms/SolutionView/SolutionSidePanel";
import { SolveStatus } from "@/components/organisms/SolutionView/SolveStatus";
import { shownSolutionTags } from "@/components/organisms/SolutionView/shownSolutionTags";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { AlternativesPanel } from "@/pages/Sbcs/components/Alternatives/AlternativesPanel";
import { AlternativesPill } from "@/pages/Sbcs/components/Alternatives/AlternativesPill";
import { AlternativesSheet } from "@/pages/Sbcs/components/Alternatives/AlternativesSheet";
import { SolutionTags } from "@/pages/Sbcs/components/Alternatives/SolutionTags";
import { AlternativeRow } from "@/pages/Sbcs/components/Alternatives/alternativeRow";
import { SummaryFigures } from "@/pages/Sbcs/components/SummaryFigures";
import { checkExtensionAvailability } from "@/redux/clubImport/clubImportSlice";
import {
  selectClubImportState,
  selectExtensionCheckReady,
  selectExtensionSupportsLatestFeatures,
} from "@/redux/clubImport/clubImportSlice";
import { addAlert, dialogSelector } from "@/redux/message/messageSlice";
import { sbcFiltersSelector } from "@/redux/sbcs/sbcFiltersSlice";
import {
  Sbc,
  Solution,
  getSbcsSelector,
  playerSwap,
  selectSolution,
} from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
  solvesWithClubSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import {
  backendErrorCode,
  errorMessage,
  useTranslation,
} from "@repo/shared/i18n";
import { SBC_SOLUTION_TAGS } from "@repo/shared/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

type SolutionViewProps = {
  solution: Solution | null;
  sbc: Sbc;
  sbcSetName?: string;
  /** The set's payout — what the challenges are being built towards. */
  setReward?: SBCRewardType;
  setDescription?: string;
  onSwap: (player: PlayerSwap, index: number) => void;
  onHandleSolveAgain: () => void;
  onHandleCompleteAndRemovePlayers: () => void;
  handleUncompleteSBC: () => void;
};

const SolutionView: React.FC<SolutionViewProps> = ({
  solution,
  sbc,
  sbcSetName,
  setReward,
  setDescription,
  onSwap,
  onHandleSolveAgain,
  onHandleCompleteAndRemovePlayers,
  handleUncompleteSBC,
}) => {
  const { t } = useTranslation("sbcSolution");
  /* The swap request reads the solve filters out of the store itself, so the
     slice is the whole dependency: a new setting cannot forget to trigger a
     refetch the way a hand-kept prop list could. */
  const filters = useSelector(sbcFiltersSelector);
  const [playerSwaps, setPlayerSwaps] = useState([] as PlayerSwap[][]);
  const [loadingSwaps, setLoadingSwaps] = useState(false);
  /* The failure panel can be closed; a new solve brings it back. */
  const [statusDismissed, setStatusDismissed] = useState(false);
  const [alternativesOpen, setAlternativesOpen] = useState(false);
  const { user } = useSelector(getUserSelector);
  const solvesWithClub = useSelector(solvesWithClubSelector);
  const { solutions, solutionStatus, solutionError } =
    useSelector(getSbcsSelector);
  // The nudge carries the solving status itself while it is up.
  const nudgeOpen =
    useSelector(dialogSelector)?.modalType === "club-import-nudge";
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const smallDesktop = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const dispatch = useDispatch<AppDispatch>();
  /* Tablets get the phone layout too: figures stacked above the pitch, no
     side rail. Card size still follows the narrower width tiers. */
  const stacked = isTouch;
  const widthClass = isMobile ? "w-16" : smallDesktop ? "w-20" : "w-24";
  const { pathname } = useLocation();

  // e.g. "EA FC 26 SBC Solution - Lukas Podolski - Germany". Drops the set part
  // when it equals the SBC name (single-SBC sets) to avoid "X - X". Names are
  // the API's, so they arrive in the content language.
  const setPart = sbcSetName ?? "";
  const sbcPart = sbc?.name ?? "...";
  const solutionTitle =
    setPart && setPart !== sbcPart
      ? t("titleWithSet", { game: GAME_NAME, set: setPart, sbc: sbcPart })
      : t("title", { game: GAME_NAME, sbc: sbcPart });

  // Real history back, never a push of the parent URL: pushing makes the
  // previous entry the page we just left, and the set page's pop-based back
  // then bounces the visitor straight back here. Deep links fall back to the
  // set page, this URL minus the sbc segments.
  const goBack = useGoBack({
    fallback: pathname.split("/").slice(0, -2).join("/"),
  });

  const canUseClubImport = useSelector(canUseClubImportSelector);
  const { extensionEnabledStatus } = useSelector(selectClubImportState);
  const extensionSupportsLatest = useSelector(
    selectExtensionSupportsLatestFeatures,
  );
  const showSendToExtension =
    canUseClubImport &&
    extensionEnabledStatus === "enabled" &&
    extensionSupportsLatest;
  const extensionCheckReady = useSelector(selectExtensionCheckReady);

  useEffect(() => {
    if (solutionStatus === "pending") setStatusDismissed(false);
  }, [solutionStatus]);

  useEffect(() => {
    if (!extensionCheckReady) return;
    dispatch(checkExtensionAvailability({ user }));
  }, [dispatch, extensionCheckReady, user]);

  const playersToRender = useMemo(() => {
    if (solutionStatus === "pending") {
      return Array.from({ length: 11 }, () => ({ placeholder: true }) as const);
    } else if (solutionStatus === "fulfilled" && solution?.players) {
      return solution?.players;
    }
    return [];
  }, [solutionStatus, solution?.players]);

  useEffect(() => {
    if (!solution || solutionStatus !== "fulfilled") return;
    const fetchPlayerSwaps = async () => {
      setLoadingSwaps(true);
      try {
        const res = await dispatch(
          playerSwap({
            id: sbc?.id,
            solutionPlayers: solution?.players,
          }),
        ).unwrap();
        setPlayerSwaps(res);
      } catch (error) {
        setPlayerSwaps([]);
        // Swaps are optional; only the sign-in gate is worth an alert.
        const code = backendErrorCode(error);
        if (code === "SIGN_IN_REQUIRED_TO_INCLUDE_OR_EXCLUDE_PLAYERS") {
          dispatch(
            addAlert({ description: errorMessage(code), type: "error" }),
          );
        }
      } finally {
        setLoadingSwaps(false);
      }
    };
    fetchPlayerSwaps();
  }, [
    dispatch,
    solution,
    solutionStatus,
    user?.uuid,
    sbc?.id,
    filters,
    solution?.players,
  ]);

  const onSwapClicked = useCallback(
    (player: PlayerSwap, index: number) => onSwap(player, index),
    [onSwap],
  );

  const reward = setReward ?? sbc?.reward;
  const requirements = sbc?.requirements ?? [];
  const solved = solutionStatus === "fulfilled" && Boolean(solution);

  /* Every pool's squad as a row, figures from the same totals as the rail.
     A club user sees the pools that found nothing greyed; a club-less user
     only ever gets the market pool, so nothing is missing for them. */
  const rows: AlternativeRow[] = useMemo(
    () =>
      solutions.map((entry) => ({
        id: entry.id,
        tags: entry.tags,
        shownTags: shownSolutionTags(entry, solutions),
        recommended: entry.recommended,
        facts: solutionFactsOf(entry),
      })),
    [solutions],
  );
  const allTags = solvesWithClub ? SBC_SOLUTION_TAGS : [];
  const onSelectSolution = (id: string) => {
    dispatch(selectSolution(id));
    setAlternativesOpen(false);
  };

  const alternatives =
    solved && solution ? (
      stacked ? (
        <AlternativesPill compact onClick={() => setAlternativesOpen(true)} />
      ) : (
        <AlternativesPanel
          rows={rows}
          allTags={allTags}
          currentId={solution.id}
          onSelect={onSelectSolution}
          stretch
        />
      )
    ) : (
      /* Greyed while the solve runs (or found nothing), so the actions keep
         their place and the list is announced before it can be opened. */
      <AlternativesPill compact={stacked} stretch={!stacked} disabled />
    );

  const actions = (
    <SolutionActions
      isCompleted={Boolean(sbc?.isCompleted)}
      isSolving={solutionStatus === "pending"}
      showSendToExtension={showSendToExtension}
      compact={stacked}
      extra={
        <SbcSheetButton
          reward={reward}
          description={setDescription}
          requirements={requirements}
        />
      }
      alternatives={alternatives}
      onSolveAgain={onHandleSolveAgain}
      onComplete={onHandleCompleteAndRemovePlayers}
      onUncomplete={handleUncompleteSBC}
    />
  );

  return (
    <div className="flex w-full flex-col bg-background">
      {/* One header for both layouts: SEO tags, the desktop bar, and the
          persistent mobile top nav. Same shape as the set page's. */}
      <HeroSection
        helmetTitle={solutionTitle}
        helmetDescription={sbc?.description || solutionTitle}
        helmetCanonicalLink={canonicalUrl()}
        back={goBack}
        hidePageDescription
        shouldBeInTheDocumentFlow
      />
      <div className="flex w-full justify-center">
        <div className="flex w-full max-w-content flex-col items-center">
          {stacked && (
            /* The rail's contents as one band above the pitch. */
            <div className="relative z-20 flex w-full flex-col gap-2.5 border-b border-edge-strong bg-surface px-4 py-2.5">
              {actions}
              <div className="h-px bg-edge-strong" />
              <SummaryFigures
                facts={solutionFactsOf(solution)}
                dense
                loading={solutionStatus === "pending"}
              />
              <SolutionTags
                tags={shownSolutionTags(solution, solutions)}
                recommended={solution?.recommended}
                loading={solutionStatus === "pending"}
              />
            </div>
          )}
          <div className="flex w-full items-stretch min-h-[calc(100vh-5rem)]">
            {!stacked && (
              <SolutionSidePanel
                facts={solutionFactsOf(solution)}
                recommended={solution?.recommended}
                tags={shownSolutionTags(solution, solutions)}
                loading={solutionStatus === "pending"}
                actions={actions}
                setReward={reward}
                setDescription={setDescription}
                requirements={requirements}
              />
            )}
            <div className={cn("min-w-0 flex-1", !stacked && "px-4")}>
              <PitchView
                sbc={sbc}
                sbcFormation={sbc?.formation}
                playersToRender={playersToRender}
                isMobile={isMobile}
                isTouch={isTouch}
                stacked={stacked}
                smallDesktop={smallDesktop}
                widthClass={widthClass}
                playerSwaps={playerSwaps}
                loadingSwaps={loadingSwaps}
                onSwapClicked={onSwapClicked}
                overlay={
                  !nudgeOpen &&
                  (solutionStatus === "pending" ||
                    (solutionStatus === "rejected" && !statusDismissed)) && (
                    <SolveStatus
                      status={solutionStatus}
                      error={solutionError}
                      onSolveAgain={onHandleSolveAgain}
                      onClose={() => setStatusDismissed(true)}
                    />
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
      {stacked && solution && (
        <AlternativesSheet
          isOpen={alternativesOpen}
          onClose={() => setAlternativesOpen(false)}
          rows={rows}
          allTags={allTags}
          currentId={solution.id}
          onSelect={onSelectSolution}
        />
      )}
    </div>
  );
};

export default SolutionView;
